import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Box } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { useTranslation } from 'react-i18next'

import OptionWithTooltip, {
  shouldShowTooltip,
} from 'components/Shared/OptionWithTooltip'
import type { Group as GroupType } from 'containers/Participants/Redux/Reducer/InitialStateType'
import { useAppSelector } from 'redux/Hook'
import { getDefaultGroup } from 'components/Participants/Group/GetDefaultGroup'

type GroupMenuItemProps = {
  group: GroupType
  isMenuItemDisabled: boolean
  handleMenuItemClick: (group: GroupType) => void
  setDeleteGroupModalVisibility: (value: boolean) => void
  setDeleteGroup: (value: GroupType) => void
  handleToggle: () => void
}

const GroupMenuItem = ({
  group,
  handleMenuItemClick,
  isMenuItemDisabled,
  setDeleteGroupModalVisibility,
  handleToggle,
  setDeleteGroup,
}: GroupMenuItemProps) => {
  const { t } = useTranslation()
  const defaultGroup = getDefaultGroup()

  const activeGroup =
    useAppSelector((state) => state.participantGroups.activeGroup) ||
    defaultGroup

  const handleDeleteGroup = async (e: any, group: GroupType) => {
    e.stopPropagation()

    handleToggle()

    setDeleteGroup(group)
    setDeleteGroupModalVisibility(true)
  }

  const DeleteIconButton = () => (
    <IconButton
      size="small"
      aria-label="delete group"
      data-testid="delete-group"
      onClick={(e) => handleDeleteGroup(e, group)}
    >
      <DeleteOutlineIcon color="textSecondary" sx={{ fontSize: '1rem' }} />
    </IconButton>
  )

  const getDeleteIconWithTooltip = () => {
    return (
      <Tooltip title={t('common.delete')}>
        <span>
          <DeleteIconButton />
        </span>
      </Tooltip>
    )
  }

  return (
    <MenuItem
      key={group.id}
      id={group.id}
      disabled={isMenuItemDisabled}
      selected={group.id === activeGroup?.id}
      onClick={() => handleMenuItemClick(group)}
    >
      {shouldShowTooltip(group.name) ? (
        <Box width="100%" display="flex" justifyContent="space-between">
          <OptionWithTooltip option={group.name} />
          {!group.defaultSegment && getDeleteIconWithTooltip()}
        </Box>
      ) : (
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {group.name}
          {!group.defaultSegment && getDeleteIconWithTooltip()}
        </Box>
      )}
    </MenuItem>
  )
}

export default GroupMenuItem
